import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MeaningAudioIcon } from "@/components/audio/MeaningAudioIcon";
import { TriLanguageIfSanskrit, TriLanguageBlock } from "@/components/i18n/TriLanguageBlock";
import {
  toTriFromFields,
  storyToTriItem,
  guruToTriItem,
  asanaToTriItem,
  sadhanaStepToTriItem,
  resolveTriLanguage,
  getPreferredMeaning,
  getEnglishMeaning,
  normalizePreferredLang,
} from "@/lib/i18n/tri-language";
import { useI18n } from "@/i18n/I18nContext";

/** Listen icon for grid cards — preferred language → English fallback. */
export function CardListenButton({
  text,
  item,
  meaningPrefix,
  preferredLanguage,
  preferredText,
  englishText,
  language,
  mode = "meaning",
  token,
  title = "Listen",
  size = 14,
  className = "",
}) {
  const { lang } = useI18n();
  const prefLang = normalizePreferredLang(preferredLanguage || lang);

  let preferred = preferredText;
  let english = englishText ?? text;
  if (item && meaningPrefix) {
    preferred = getPreferredMeaning(item, prefLang, meaningPrefix);
    english = getEnglishMeaning(item, meaningPrefix) || english;
  } else {
    preferred = preferred ?? text;
  }

  const listenText = (preferred || english || "").trim();
  if (!listenText) return null;

  const listenLang =
    prefLang !== "en" && preferred && english && preferred !== english ? prefLang : "en";

  return (
    <span
      className={`inline-flex shrink-0 ${className}`}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      role="presentation"
    >
      <MeaningAudioIcon
        text={listenText}
        language={listenLang}
        fallbackText={english}
        fallbackLanguage="en"
        mode={mode}
        token={token}
        title={title}
        size={size}
      />
    </span>
  );
}

/** Story library card with tri-language summary preview. */
export function StoryCard({ story, onClick, token, className = "" }) {
  const title = story?.canonical?.title;
  const source = story?.canonical?.source_text;
  const triItem = storyToTriItem(story);

  return (
    <Card
      className={`cursor-pointer hover:shadow-float transition-all bg-white/80 ${className}`}
      onClick={onClick}
      data-testid={`story-card-${story.id}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-heading text-lg font-medium">{title}</h3>
          <CardListenButton item={triItem} meaningPrefix="summary" mode="story" token={token} title="Listen to story" />
        </div>
        <div className="mb-3 line-clamp-3 text-sm">
          <TriLanguageBlock item={triItem} meaningPrefix="summary" compact token={token} />
        </div>
        <div className="flex justify-between items-center">
          <Badge variant="secondary">{source}</Badge>
          <span className="text-xs text-muted-foreground">Click to read</span>
        </div>
      </CardContent>
    </Card>
  );
}

/** Guru profile card with tri-language intro. */
export function GuruCard({ guru, onClick, token, className = "" }) {
  const title = `${guru?.canonical?.title ?? ""} ${guru?.canonical?.name ?? ""}`.trim();
  const triItem = guruToTriItem(guru);

  return (
    <Card
      className={`cursor-pointer hover:shadow-float transition-all bg-white/80 ${className}`}
      onClick={onClick}
      data-testid={`guru-card-${guru.id}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-heading text-xl font-medium">{title}</h3>
          <CardListenButton item={triItem} meaningPrefix="translation" mode="commentary" token={token} title="Listen to guru intro" />
        </div>
        <div className="mb-3 line-clamp-3 text-sm">
          <TriLanguageBlock item={triItem} meaningPrefix="translation" compact token={token} />
        </div>
        <Badge variant="outline">{guru?.canonical?.lineage}</Badge>
      </CardContent>
    </Card>
  );
}

/** Knowledge base topic card with tri-language summary. */
export function KnowledgeBaseCard({ item, onClick, token, className = "" }) {
  const triItem = toTriFromFields({
    sanskrit: item?.sanskrit,
    english: item?.simple,
    summary_en: item?.simple,
    translation_en: item?.moderate,
    ...item,
  });

  return (
    <Card
      className={`cursor-pointer hover:shadow-float bg-white/80 ${className}`}
      onClick={onClick}
      data-testid={`knowledge-topic-${item.id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-heading text-lg font-medium">{item.title}</h3>
          <CardListenButton item={triItem} meaningPrefix="summary" mode="meaning" token={token} title="Listen to topic" />
        </div>
        <TriLanguageBlock item={triItem} meaningPrefix="summary" compact token={token} className="mt-2" />
      </CardContent>
    </Card>
  );
}

/** Yoga āsana card with tri-language description. */
export function YogaAsanaCard({ asana, onClick, token, className = "" }) {
  const triItem = asanaToTriItem(asana);

  return (
    <Card
      className={`cursor-pointer hover:shadow-float bg-white/80 ${className}`}
      onClick={onClick}
      data-testid={`yoga-asana-card-${asana.id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-heading text-lg font-medium">{asana.name_common || asana.name || asana.title}</h3>
          <CardListenButton
            item={triItem}
            meaningPrefix="description"
            mode="sadhana"
            token={token}
            title="Listen to āsana instruction"
          />
        </div>
        <TriLanguageBlock item={triItem} meaningPrefix="description" compact token={token} className="mb-2" />
      </CardContent>
    </Card>
  );
}

/** Single sādhana instruction step with tri-language meaning. */
export function SadhanaStep({ text, step, index, token, className = "" }) {
  const item = step ?? sadhanaStepToTriItem(text);
  const tri = resolveTriLanguage(item, "en", { meaningPrefix: "meaning" });
  const content = tri.preferred || tri.english;
  if (!content && !tri.sanskrit_devanagari) return null;

  return (
    <li className={`flex items-start gap-2 ${className}`} data-testid="sadhana-step">
      <span className="text-primary shrink-0">{typeof index === "number" ? `${index + 1}.` : "•"}</span>
      <div className="flex-1 min-w-0">
        <TriLanguageBlock item={item} meaningPrefix="meaning" meaningAudioMode="sadhana" compact token={token} />
      </div>
    </li>
  );
}
